import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { withCn, withProps } from '@udecode/cn';
import React from 'react';

import { ToolbarButton } from '../toolbar/toolbarButton.component';

export const TooltipProvider = withProps(TooltipPrimitive.Provider, {
    delayDuration: 0,
    disableHoverableContent: true,
    skipDelayDuration: 0,
});

export const Tooltip = TooltipPrimitive.Root;

export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipPortal = TooltipPrimitive.Portal;

export const TooltipContent = withCn(
    withProps(TooltipPrimitive.Content, {
        sideOffset: 4,
    }),
    'z-[100] overflow-hidden rounded-md bg-black px-3 py-1.5 text-sm font-semibold text-white shadow-md'
);

type TooltipProps<T extends React.ElementType> = {
  delayDuration?: number;
  disableHoverableContent?: boolean;
  skipDelayDuration?: number;
  tooltip?: React.ReactNode;
  tooltipContentProps?: Omit<
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    'children'
  >;
  tooltipProps?: Omit<
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>,
    'children'
  >;
  tooltipTriggerProps?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Trigger
  >;
  className?: string;
  variant?: string;
} & React.ComponentProps<T>;

export function withTooltip<T extends React.ElementType>(Component: T) {
    const ExtendComponent = React.forwardRef<any, TooltipProps<T>>(
        (
            {
                delayDuration = 0,
                disableHoverableContent = true,
                skipDelayDuration = 0,
                tooltip,
                tooltipContentProps,
                tooltipProps,
                tooltipTriggerProps,
                ...props
            },
            ref
        ) => {
            const [mounted, setMounted] = React.useState(false);

            React.useEffect(() => {
                setMounted(true);
            }, []);

            // Resolve the component if it's an object with a default export.
            const ResolvedComponent = (Component as any).default || Component;

            // Create the element with forwarded ref.
            const component = React.createElement(ResolvedComponent, { ...props, ref });

            if (tooltip && mounted) {
                return (
                    <TooltipProvider
                        delayDuration={delayDuration}
                        disableHoverableContent={disableHoverableContent}
                        skipDelayDuration={skipDelayDuration}
                    >
                        <Tooltip {...tooltipProps}>
                            <TooltipTrigger asChild {...tooltipTriggerProps}>
                                {component}
                            </TooltipTrigger>
                            <TooltipPortal>
                                <TooltipContent {...tooltipContentProps}>
                                    {tooltip}
                                </TooltipContent>
                            </TooltipPortal>
                        </Tooltip>
                    </TooltipProvider>
                );
            }

            return component;
        }
    );

    return ExtendComponent as React.FC<TooltipProps<T>>;
}

export const TooltipToolbarButton = withTooltip(ToolbarButton);

export const TooltipButton = withTooltip(
    React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<'button'>>(
        (props: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>, ref) => <button ref={ref} {...props} />
    )
);
